import { CUSTOM_DATA_TYPE_ICONS } from "../../constants/customDataTypeIcons";
import { BACKEND_FIELD_TYPE_KEYS } from "./constants";

/**
 * Static metadata per field type. Strategies (normalize, serialize, coerce) live in separate modules.
 * importable defaults to true for all registered types so new backend types support import once registered.
 */
const BASE_REGISTRY = {
  STRING: {
    label: "Text",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomString",
    defaultValue: "",
    iconEmoji: "📝",
    valueShape: "scalar",
    semanticAliases: [
      "IDENTIFIER",
      "PERSON_NAME",
      "PHONE",
      "EMAIL",
      "ADDRESS",
      "CITY",
      "COUNTRY",
      "POSTAL_CODE",
      "DURATION",
      "STATUS",
      "CATEGORY",
      "NOTES",
      "CUSTOM",
      "PRODUCT_CODE",
      "SKU",
    ],
  },
  URL: {
    label: "URL",
    javaClass: "com.horizon.gmap.demo.data.CustomURL",
    defaultValue: "",
    iconEmoji: "🔗",
    valueShape: "scalar",
    semanticAliases: ["URL"],
  },
  INTEGER: {
    label: "Number",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomInteger",
    defaultValue: null,
    iconEmoji: "🔢",
    valueShape: "scalar",
    semanticAliases: ["QUANTITY"],
  },
  DOUBLE: {
    label: "Decimal",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomDouble",
    defaultValue: null,
    iconEmoji: "🔣",
    valueShape: "scalar",
    semanticAliases: ["PERCENTAGE"],
  },
  CURRENCY: {
    label: "Currency",
    javaClass: "com.horizon.gmap.demo.data.CurrencyValue",
    defaultValue: { amount: "", currencyCode: "" },
    iconEmoji: "💱",
    valueShape: "object",
    composite: true,
    semanticAliases: ["MONEY_AMOUNT", "CURRENCY"],
  },
  BOOLEAN: {
    label: "True/False",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomBoolean",
    defaultValue: null,
    iconEmoji: "☑️",
    valueShape: "scalar",
    semanticAliases: [],
  },
  DATE: {
    label: "Date",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomDate",
    defaultValue: null,
    iconEmoji: "📅",
    valueShape: "scalar",
    semanticAliases: ["DATE"],
  },
  TIME: {
    label: "Time",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomTime",
    defaultValue: null,
    iconEmoji: "⏱️",
    valueShape: "scalar",
    semanticAliases: ["TIME"],
  },
  DATETIME: {
    label: "Date & Time",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomDateTime",
    defaultValue: null,
    iconEmoji: "🗓️",
    valueShape: "scalar",
    semanticAliases: ["DATETIME"],
  },
  PERIOD: {
    label: "Period",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomPeriod",
    defaultValue: { startDate: null, endDate: null },
    iconEmoji: "📅→📅",
    valueShape: "range",
    composite: true,
    semanticAliases: [],
  },
  STRING_ARRAY: {
    label: "Text List",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomStringArray",
    defaultValue: [],
    iconEmoji: "🧾",
    valueShape: "array",
    semanticAliases: [],
  },
  ENUM: {
    label: "Dropdown",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomEnum",
    defaultValue: null,
    iconEmoji: "🔽",
    valueShape: "scalar",
    hasOptions: true,
    semanticAliases: [],
  },
  COMBOLIST: {
    label: "Combo List",
    javaClass: "com.horizon.gmap.demo.model.CustomFields.CustomComboList",
    defaultValue: null,
    iconEmoji: "📋",
    valueShape: "scalar",
    hasOptions: true,
    semanticAliases: [],
  },
};

const buildRegistryEntry = (key, meta) => ({
  key,
  ...meta,
  iconClass: CUSTOM_DATA_TYPE_ICONS[key] || "fi fi-rr-text",
  capabilities: {
    editableInApp: true,
    importable: true,
    hasOptions: Boolean(meta.hasOptions),
    ...(meta.capabilities || {}),
  },
});

export const FIELD_TYPE_REGISTRY = Object.fromEntries(
  BACKEND_FIELD_TYPE_KEYS.map((key) => {
    const meta = BASE_REGISTRY[key];
    if (!meta) {
      throw new Error(`Missing registry metadata for backend field type: ${key}`);
    }
    return [key, buildRegistryEntry(key, meta)];
  })
);

export const FIELD_DEFINITIONS = Object.fromEntries(
  Object.entries(FIELD_TYPE_REGISTRY).map(([key, entry]) => [
    key,
    {
      key: entry.key,
      simpleType: entry.label,
      javaClass: entry.javaClass,
      defaultValue: entry.defaultValue,
      icon: entry.iconEmoji,
      iconClass: entry.iconClass,
      capabilities: entry.capabilities,
    },
  ])
);
